import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Input } from '../../components/ui/input';
import { Loader2, Download, CalendarIcon, FileSpreadsheet, Truck, Package, ChevronDown, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import AdminLayout from '../../components/AdminLayout';

const API_URL = window.location.origin;

const AdminInvoicing = () => {
  const [orders, setOrders] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [selectedOrgs, setSelectedOrgs] = useState(new Set());
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOrgDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [ordersRes, orgsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/invoicing/organisations`, { withCredentials: true })
      ]);
      setOrders(ordersRes.data);
      setOrganisations(orgsRes.data);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleOrg = (org) => {
    const next = new Set(selectedOrgs);
    if (next.has(org)) next.delete(org);
    else next.add(org);
    setSelectedOrgs(next);
  };

  const selectAllOrgs = () => {
    if (selectedOrgs.size === organisations.length) setSelectedOrgs(new Set());
    else setSelectedOrgs(new Set(organisations));
  };

  const filteredOrgList = organisations.filter(o =>
    o.toLowerCase().includes(orgSearch.toLowerCase())
  );

  // Filtered orders for preview
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      if (dateFrom) {
        const dw = o.delivery_week || '';
        if (dw < format(dateFrom, 'yyyy-MM-dd')) return false;
      }
      if (dateTo) {
        const dw = o.delivery_week || '';
        if (dw > format(dateTo, 'yyyy-MM-dd')) return false;
      }
      if (selectedOrgs.size > 0 && !selectedOrgs.has(o.organisation_name)) return false;
      return true;
    });
  }, [orders, dateFrom, dateTo, selectedOrgs]);

  // Summary stats
  const summary = useMemo(() => {
    let chilled = 0, hte = 0, inclBasic = 0, inclPremium = 0, addtlBasic = 0, addtlPremium = 0, standard = 0, premium = 0;
    for (const o of filteredOrders) {
      const dopt = (o.delivery_option || 'Standard').toLowerCase();
      if (dopt.includes('premium')) premium++;
      else standard++;
      for (const item of (o.items || [])) {
        const tag = (item.meal_type_tag || '').toLowerCase();
        if (tag.includes('heat to eat')) hte += item.quantity || 0;
        else chilled += item.quantity || 0;
      }
      for (const a of (o.addon_selections || [])) {
        const qty = a.quantity || 0;
        const isPrem = a.addon_type === 'premium';
        if (a.is_additional) {
          if (isPrem) addtlPremium += qty;
          else addtlBasic += qty;
        } else {
          if (isPrem) inclPremium += qty;
          else inclBasic += qty;
        }
      }
    }
    return {
      chilled, hte, inclBasic, inclPremium, addtlBasic, addtlPremium,
      total: chilled + hte + inclBasic + inclPremium + addtlBasic + addtlPremium,
      standard, premium, orderCount: filteredOrders.length
    };
  }, [filteredOrders]);

  // Group by week for preview table
  const weeklyData = useMemo(() => {
    const map = {};
    for (const o of filteredOrders) {
      const dw = o.delivery_week || 'Unknown';
      if (!map[dw]) map[dw] = [];
      map[dw].push(o);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredOrders]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = format(dateFrom, 'yyyy-MM-dd');
      if (dateTo) params.date_to = format(dateTo, 'yyyy-MM-dd');
      if (selectedOrgs.size > 0) params.organisations = Array.from(selectedOrgs).join(',');

      const response = await axios.get(`${API_URL}/api/admin/invoicing/export`, {
        withCredentials: true, responseType: 'blob', params
      });

      // Get filename from Content-Disposition or build one
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename=(.+?)(?:;|$)/);
      const serverFilename = match ? match[1] : null;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', serverFilename || `munchd_invoice_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice exported successfully');
    } catch (e) {
      toast.error('Failed to export invoice');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Invoicing</h1>
            <p className="text-sm text-slate-500 mt-1">Generate invoice reports by date range and organisation</p>
          </div>
          <Button onClick={handleExport} disabled={exporting || filteredOrders.length === 0}
            className="rounded-full bg-green-600 hover:bg-green-700 w-full sm:w-auto" data-testid="export-invoice-button">
            {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
              : <><Download className="w-4 h-4 mr-2" />Export Invoice</>}
          </Button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Date Range */}
          <Card className="border-slate-200/60">
            <CardContent className="p-4 space-y-3">
              <h3 className="text-sm font-medium text-slate-700">Delivery Date Range</h3>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-start text-left rounded-xl h-10" data-testid="date-from-picker">
                      <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                      {dateFrom ? format(dateFrom, 'dd MMM yyyy') : <span className="text-slate-400">From</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
                  </PopoverContent>
                </Popover>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="flex-1 justify-start text-left rounded-xl h-10" data-testid="date-to-picker">
                      <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
                      {dateTo ? format(dateTo, 'dd MMM yyyy') : <span className="text-slate-400">To</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
                  </PopoverContent>
                </Popover>
              </div>
              {(dateFrom || dateTo) && (
                <Button variant="ghost" size="sm" onClick={() => { setDateFrom(null); setDateTo(null); }}
                  className="text-xs text-slate-500">Clear dates</Button>
              )}
            </CardContent>
          </Card>

          {/* Organisation Multi-select Dropdown */}
          <Card className="border-slate-200/60 lg:col-span-2">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-slate-700">Organisations</h3>
                {selectedOrgs.size > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setSelectedOrgs(new Set())}
                    className="text-xs text-slate-500 h-6 px-2" data-testid="clear-orgs-button">
                    <X className="w-3 h-3 mr-1" />Clear ({selectedOrgs.size})
                  </Button>
                )}
              </div>

              <div className="relative" ref={dropdownRef}>
                <Button
                  variant="outline"
                  className="w-full justify-between rounded-xl h-10 text-left"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  data-testid="org-dropdown-trigger"
                >
                  <span className={selectedOrgs.size === 0 ? 'text-slate-400' : 'text-slate-900'}>
                    {selectedOrgs.size === 0
                      ? 'Select organisations...'
                      : selectedOrgs.size === organisations.length
                        ? 'All organisations'
                        : `${selectedOrgs.size} organisation${selectedOrgs.size !== 1 ? 's' : ''} selected`}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${orgDropdownOpen ? 'rotate-180' : ''}`} />
                </Button>

                {orgDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-[280px] overflow-hidden flex flex-col">
                    {/* Search */}
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          placeholder="Search organisations..."
                          value={orgSearch}
                          onChange={(e) => setOrgSearch(e.target.value)}
                          className="pl-8 h-8 rounded-lg text-sm"
                          data-testid="org-search-input"
                        />
                      </div>
                    </div>

                    {/* Select All */}
                    <div className="px-2 py-1.5 border-b border-slate-100">
                      <label className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-slate-50 cursor-pointer text-sm font-medium text-slate-700">
                        <Checkbox
                          checked={selectedOrgs.size === organisations.length && organisations.length > 0}
                          onCheckedChange={selectAllOrgs}
                          className="h-3.5 w-3.5"
                          data-testid="select-all-orgs"
                        />
                        Select All ({organisations.length})
                      </label>
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto flex-1 p-1">
                      {filteredOrgList.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-3">No matches</p>
                      ) : (
                        filteredOrgList.map(org => (
                          <label key={org}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer text-sm"
                            data-testid={`org-option-${org.replace(/\s+/g, '-')}`}>
                            <Checkbox
                              checked={selectedOrgs.has(org)}
                              onCheckedChange={() => toggleOrg(org)}
                              className="h-3.5 w-3.5"
                            />
                            <span className="truncate">{org}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected tags preview */}
              {selectedOrgs.size > 0 && selectedOrgs.size <= 6 && (
                <div className="flex flex-wrap gap-1.5">
                  {Array.from(selectedOrgs).map(org => (
                    <Badge key={org} className="bg-green-50 text-green-700 border border-green-200 text-xs cursor-pointer hover:bg-green-100"
                      onClick={() => toggleOrg(org)}>
                      {org} <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <Package className="w-4 h-4 text-green-600 mx-auto mb-1" />
              <p className="text-xl font-bold text-slate-900">{summary.total}</p>
              <p className="text-[10px] text-slate-500">Total Meals</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-blue-700">{summary.chilled}</p>
              <p className="text-[10px] text-slate-500">Chilled/Hybrid</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <p className="text-xl font-bold text-orange-600">{summary.hte}</p>
              <p className="text-[10px] text-slate-500">Heat to Eat</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-emerald-600">{summary.inclBasic}</p>
              <p className="text-[10px] text-slate-500">Incl. Basic</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-purple-600">{summary.inclPremium}</p>
              <p className="text-[10px] text-slate-500">Incl. Premium</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <p className="text-lg font-bold text-amber-600">{summary.addtlBasic + summary.addtlPremium}</p>
              <p className="text-[10px] text-slate-500">Additional</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200/60">
            <CardContent className="p-3 text-center">
              <Truck className="w-4 h-4 text-slate-500 mx-auto mb-1" />
              <p className="text-lg font-bold text-slate-700">{summary.standard}/{summary.premium}</p>
              <p className="text-[10px] text-slate-500">Std/Prem</p>
            </CardContent>
          </Card>
        </div>

        {/* Weekly Breakdown Table */}
        <Card className="border-slate-200/60 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Delivery Week</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-center">Chilled/Hybrid</TableHead>
                    <TableHead className="text-center">HTE</TableHead>
                    <TableHead className="text-center">Incl. Basic</TableHead>
                    <TableHead className="text-center">Incl. Premium</TableHead>
                    <TableHead className="text-center">Addtl. Basic</TableHead>
                    <TableHead className="text-center">Addtl. Premium</TableHead>
                    <TableHead className="text-center">Total</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Delivery</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                        <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        No orders match the selected filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    weeklyData.map(([week, weekOrders]) => (
                      weekOrders.map((order, idx) => {
                        let chilled = 0, hte = 0, iBasic = 0, iPremium = 0, aBasic = 0, aPremium = 0;
                        for (const item of (order.items || [])) {
                          if ((item.meal_type_tag || '').toLowerCase().includes('heat to eat')) hte += item.quantity || 0;
                          else chilled += item.quantity || 0;
                        }
                        for (const a of (order.addon_selections || [])) {
                          const qty = a.quantity || 0;
                          const isPrem = a.addon_type === 'premium';
                          if (a.is_additional) { if (isPrem) aPremium += qty; else aBasic += qty; }
                          else { if (isPrem) iPremium += qty; else iBasic += qty; }
                        }
                        const total = chilled + hte + iBasic + iPremium + aBasic + aPremium;
                        let weekLabel;
                        try { weekLabel = format(new Date(week), 'dd MMM yyyy'); } catch { weekLabel = week; }

                        return (
                          <TableRow key={order.id} data-testid={`invoice-row-${order.id}`}>
                            <TableCell>{idx === 0 ? <span className="font-medium">{weekLabel}</span> : ''}</TableCell>
                            <TableCell>{order.organisation_name}</TableCell>
                            <TableCell className="text-sm text-slate-500">{order.user_username}</TableCell>
                            <TableCell className="text-center">{chilled || '-'}</TableCell>
                            <TableCell className="text-center">{hte || '-'}</TableCell>
                            <TableCell className="text-center">{iBasic || '-'}</TableCell>
                            <TableCell className="text-center">{iPremium || '-'}</TableCell>
                            <TableCell className="text-center">{aBasic || '-'}</TableCell>
                            <TableCell className="text-center">{aPremium || '-'}</TableCell>
                            <TableCell className="text-center font-semibold">{total}</TableCell>
                            <TableCell className="text-xs text-slate-500">{order.catering_plan}</TableCell>
                            <TableCell>
                              <Badge className={
                                (order.delivery_option || '').toLowerCase().includes('premium')
                                  ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-600'
                              }>
                                {(order.delivery_option || 'Standard').includes('Premium') ? 'Premium' : 'Standard'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminInvoicing;
